import "@/styles/application.css";
import { createInertiaApp, router } from "@inertiajs/react";
import type { Page } from "@inertiajs/core";
import { createRoot } from "react-dom/client";
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { LONG_PRESS_MOVE_TOLERANCE_PX } from "@/hooks/useLongPress";
import UpdateBanner from "@/components/UpdateBanner";
import ForeignOutboxConfirm from "@/components/ForeignOutboxConfirm";
import { collisionDetection } from "@/lib/collision";
import { syncForUser } from "@/lib/syncEngine";
import { deleteLegacySnapshots } from "@/lib/localDb";
import { watchOfflineNavigation } from "@/lib/offlineNavigation";
import type { SharedProps } from "@/types";

function syncForPageUser(page: Page): void {
  void syncForUser((page.props as SharedProps).currentUser?.id ?? null);
}

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<{ default: React.ComponentType }>(
      ["../pages/**/*.tsx", "!../pages/**/*.test.tsx"],
      { eager: true },
    );
    const page = pages[`../pages/${name}.tsx`];
    if (!page) throw new Error(`Inertia page not found: ${name}`);
    return page.default;
  },
  setup({ el, App, props }) {
    deleteLegacySnapshots();
    syncForPageUser(props.initialPage);
    router.on("navigate", (event) => syncForPageUser(event.detail.page));
    watchOfflineNavigation();
    function Root() {
      // MouseSensor + TouchSensor, not PointerSensor: on touch, pointerdown fires
      // before touchstart, so PointerSensor would claim the gesture first and its
      // distance constraint never fires on a stationary long-press — leaving mobile
      // without long-press drag entirely.
      const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: LONG_PRESS_MOVE_TOLERANCE_PX } }),
      );
      return (
        <DndContext sensors={sensors} collisionDetection={collisionDetection}>
          <App {...props} />
          <UpdateBanner />
          <ForeignOutboxConfirm />
        </DndContext>
      );
    }
    createRoot(el).render(<Root />);
  },
  progress: { color: "#3b82f6" },
});
